import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import api from '../../utils/api';

export const meta = {
  id: 'open_by_agent',
  category: 'tickets',
  titleKey: 'dashboard.widgets.openByAgent',
  descriptionKey: 'dashboard.widgets.openByAgentDesc',
  defaultSize: 'md',
  minRole: 'admin',
  icon: Users,
};

// Reuses the /presence/agents query that AgentPresenceWidget already runs,
// so when both widgets are visible there's zero extra fetch.
export default function OpenByAgentWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: agents = [] } = useQuery({
    queryKey: ['agent-presence'],
    queryFn: () => api.get('/presence/agents').then(r => r.data),
    refetchInterval: 30000,
  });

  const sorted = [...agents]
    .map(a => ({ ...a, open: parseInt(a.open_tickets) || 0, support: parseInt(a.support_open) || 0 }))
    .filter(a => a.open > 0 || a.support > 0)
    .sort((a, b) => (b.open - a.open) || (b.support - a.support))
    .slice(0, 8);
  const max = Math.max(1, ...sorted.map(a => a.open));

  return (
    <div className="card" style={{ height: '100%' }}>
      <div className="card-header">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Users size={16} />
          {t('dashboard.widgets.openByAgent')}
        </div>
      </div>
      {sorted.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>—</div>
      ) : (
        <div style={{ padding: '4px 16px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sorted.map(agent => {
            const pct = Math.max(6, Math.round((agent.open / max) * 100));
            return (
              <div
                key={agent.id}
                onClick={() => navigate(`/tickets?assigned_to=${agent.id}&status=__active__`)}
                style={{
                  cursor: 'pointer',
                  padding: '6px 10px',
                  borderRadius: 6,
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--c-surface-2)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                <div style={{
                  position: 'absolute', inset: 0,
                  background: `linear-gradient(to right, rgba(59, 130, 246, 0.12) ${pct}%, transparent ${pct}%)`,
                  pointerEvents: 'none',
                }} />
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div className="user-avatar" style={{ width: 24, height: 24, fontSize: 10, flexShrink: 0 }}>
                    {agent.avatar_url
                      ? <img src={agent.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                      : agent.name?.[0] || '?'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {agent.name}
                  </div>
                  {agent.support > 0 && (
                    <span
                      title={t('dashboard.widgets.supportTickets', { count: agent.support })}
                      style={{
                        fontSize: 10, fontWeight: 600, color: '#8b5cf6', flexShrink: 0,
                        background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.3)',
                        padding: '1px 6px', borderRadius: 8,
                      }}
                    >
                      +{agent.support}
                    </span>
                  )}
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--c-brand)', flexShrink: 0 }}>
                    {agent.open}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
